import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
import { toast, Toaster } from "sonner";
import {
  CheckCircle,
  AlertCircle,
  FileText,
  User,
  Building,
  Download,
  ShieldCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  medical_records: "Medical Records",
  plan_of_care: "Plan of Care",
  billing_records: "Billing Records",
  mental_health: "Mental Health Records",
  substance_abuse: "Substance Abuse Records",
  hiv_aids: "HIV / AIDS Information",
  lab_results: "Lab Results",
  other: "Other",
};

export default function ROISignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [relationship, setRelationship] = useState("Self");

  useEffect(() => {
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verify = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/roi/verify/${token}`);
      setForm(res.data);
      const client = res.data.client_snapshot || {};
      setSignerName(`${client.first_name || ""} ${client.last_name || ""}`.trim());
      if (res.data.status === "signed") setSigned(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("This link is invalid or has been revoked.");
      } else {
        setError("Unable to load this document. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (signatureData) => {
    if (!signerName.trim()) {
      toast.error("Please enter your full name before signing");
      return;
    }
    setSigning(true);
    try {
      await axios.post(`${API}/roi/submit/${token}`, {
        signature: signatureData,
        signer_name: signerName.trim(),
        signer_relationship: relationship,
      });
      toast.success("Thank you! Your signature has been recorded.");
      setSigned(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit signature");
    } finally {
      setSigning(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${API}/roi/public/${token}/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Verifying secure link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="roi-error-card">
          <CardHeader className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-600">Link Invalid</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (signed) {
    const signedAccent = form?.agency_snapshot?.branding?.accent_color || "#0f172a";
    const signedLogo = form?.agency_snapshot?.branding?.logo_data_url;
    return (
      <div className="min-h-screen bg-slate-50">
        <div style={{ background: signedAccent }} className="h-1.5 w-full" />
        <div className="flex items-center justify-center p-4 pt-12">
          <Card className="max-w-md w-full" data-testid="roi-signed-card">
            <CardHeader className="text-center">
              {signedLogo && (
                <img
                  src={signedLogo}
                  alt="Agency logo"
                  className="mx-auto mb-3 max-h-12 object-contain"
                />
              )}
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <CardTitle className="text-emerald-600">Document Signed</CardTitle>
              <CardDescription>
                Thank you. Your Release of Information has been recorded with{" "}
                {form?.agency_snapshot?.name || "the agency"}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full"
                variant="outline"
                onClick={downloadPdf}
                data-testid="roi-download-signed-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Signed Copy (PDF)
              </Button>
              <p className="text-center text-xs text-slate-400">
                You may close this window after downloading your copy.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const agency = form?.agency_snapshot || {};
  const client = form?.client_snapshot || {};
  const cats = form?.information_categories || [];
  const branding = agency.branding || {};
  const accent = branding.accent_color || "#0f172a";

  return (
    <div className="min-h-screen bg-slate-50">
      <div style={{ background: accent }} className="h-1.5 w-full" data-testid="roi-accent-bar" />
      <div className="py-8 px-4">
      <Toaster position="top-center" />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          {branding.logo_data_url ? (
            <img
              src={branding.logo_data_url}
              alt={agency.name || "Agency logo"}
              className="mx-auto mb-3 max-h-16 max-w-xs object-contain"
              data-testid="roi-branding-logo"
            />
          ) : (
            <Building className="w-8 h-8 mx-auto text-slate-700 mb-2" />
          )}
          <h1 className="text-2xl font-bold text-slate-800">
            {agency.name || "Healthcare Agency"}
          </h1>
          <p className="text-slate-500 text-sm">Authorization for Release of Information</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document Summary
            </CardTitle>
            <CardDescription>
              Please review the details below before signing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Info label="Patient" value={`${client.first_name || ""} ${client.last_name || ""}`} />
              <Info label="Date of Birth" value={client.dob || "—"} />
              <Info label="Recipient of Disclosure" value={form?.recipient_name || "—"} />
              <Info label="Purpose" value={form?.purpose || "Continuity of Care"} />
              <Info label="Expiration" value={form?.expiration || "Until Revoked"} />
              {agency.phone && <Info label="Agency Phone" value={agency.phone} />}
            </div>

            <div>
              <Label className="text-xs text-slate-500">Information Authorized for Disclosure</Label>
              <ul className="mt-1 text-sm text-slate-800 list-disc pl-5">
                {cats.length === 0 && <li className="text-slate-400 list-none">None selected</li>}
                {cats.map((c) => (
                  <li key={c}>{CATEGORY_LABELS[c] || c}</li>
                ))}
              </ul>
              {form?.other_information && (
                <p className="mt-2 text-sm text-slate-700">
                  <span className="font-semibold">Other:</span> {form.other_information}
                </p>
              )}
            </div>

            {form?.additional_notes && (
              <div>
                <Label className="text-xs text-slate-500">Additional Notes</Label>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{form.additional_notes}</p>
              </div>
            )}

            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 leading-relaxed">
              <ShieldCheck className="w-4 h-4 inline mr-1 -mt-1 text-slate-500" />
              I understand I may revoke this authorization at any time in writing. The information
              disclosed may be subject to redisclosure by the recipient and may no longer be
              protected by HIPAA. Signing this form is voluntary.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Your Signature
            </CardTitle>
            <CardDescription>
              Sign electronically to authorize the release described above.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="signer-name">Full Legal Name</Label>
                <Input
                  id="signer-name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Your full name"
                  data-testid="roi-signer-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Signing as</Label>
                <Select value={relationship} onValueChange={setRelationship}>
                  <SelectTrigger data-testid="roi-relationship-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Self">Self (the patient)</SelectItem>
                    <SelectItem value="Legal Guardian">Legal Guardian</SelectItem>
                    <SelectItem value="Power of Attorney">Power of Attorney</SelectItem>
                    <SelectItem value="Parent">Parent</SelectItem>
                    <SelectItem value="Spouse">Spouse</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Draw Signature</Label>
              <SignaturePad onSave={handleSign} width={500} height={180} />
            </div>

            {signing && (
              <div className="text-center text-slate-500">
                <div className="w-6 h-6 border-2 border-slate-800 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm mt-2">Submitting signature...</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-xs text-slate-500">
          Secure electronic signature request from {agency.name || "the healthcare agency"}.
        </div>
      </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg">
      <Label className="text-xs text-slate-500">{label}</Label>
      <p className="text-sm font-medium text-slate-800">{value || "—"}</p>
    </div>
  );
}
